import { DotButton, NextButton, PrevButton } from './CarouselButtons';
import { OmakaseColorsStringType } from 'theme/colors/omakaseColors';
import { OmakaseTypographyVariant } from 'theme/typography/omakaseTypography';
import { Property } from 'csstype';
import { Theme, css } from '@emotion/react';
import {
  carouselBase,
  carouselButtonGradient,
  carouselContainer,
  carouselSlide,
  carouselSlideImg,
  carouselSlideInner,
  carouselSlideText,
  carouselSlideTextInner,
  carouselViewport,
  dotBtnsContainer,
  dotBtnsContainerInner,
} from './carouselCssDefs';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

export interface CarouselProps {
  slides: Array<SlideProps>;
  /**
   * Should the slides loop from the "end" to the "beginning" again and vice-versa. Default behavior matches "false"
   */
  loop?: boolean;
  /**
   * Should we show the arrows at mobile widths? Default behavior matches "false"
   */
  showArrowsOnMobile?: boolean;
  showButtonGradient?: boolean;
  objectFit?: Property.ObjectFit;
  buttonPadding?: CarouselButtonPadding;
  // Whether to show the dot buttons and arrows at all if there is only a single slide
  // to display. Defaults to false
  showControlsForSingleSlide?: boolean;

  slideClickHandling?: {
    onClick: (slideIndex: number) => void;
    role: React.AriaRole;
    'aria-label': string;
  };
  onSlideChanged?(slideIndex: number): void;

  /**
   * Optional prop from parent to force arrow visibility on desktop (i.e. tying to parent component focus state)
   */
  showArrowsDesktop?: 'always' | 'onmouseover' | 'never';
  /**
   * Optional prop to specify how many indicator dots to show.
   * Must be odd number to ensure optical centering (defaults to 5)
   */
  numDotsToDisplay?: number;
}

// Exported for test/convenience only, not a crucial export.
export type SlideProps = ImageSlideProps | TextSlideProps;

export interface ImageSlideProps {
  src: string;
  srcSet?: string;
  alt: string;
  type: 'image';
  padding?: number;
}

export interface TextSlideProps {
  // TODO(matthew-tock): consider allowing for arbitrary React.ReactNode content rather than
  // just string.
  content: string;
  variant: OmakaseTypographyVariant;
  fgColor: keyof OmakaseColorsStringType;
  bgColor: keyof OmakaseColorsStringType;
  /** behavior for edgeSpacing will default to 6 spacing units if not specified. Specifying
   *  a value < 6 will also be clamped to exactly 6.
   */
  edgeSpacing?: number;
  type: 'text';
}

export type CarouselButtonPadding = 1 | 2;

type SlideDetailsProps = SlideProps & {
  index: number;
  selectedIndex: number;
  // Note that onClick will be defined iff role and aria-label are defined.
  onClick?: (index: number) => void;
  role?: React.AriaRole;
  objectFit: Property.ObjectFit;
  'aria-label'?: string;
};

const SlideDetails: FunctionComponent<SlideDetailsProps> = (props) => {
  const { index, selectedIndex, type, objectFit, onClick, role, 'aria-label': ariaLabel } = props;
  let innerContent;
  if (type === 'image') {
    const { src, srcSet, alt } = props;
    innerContent = (
      <img
        src={src}
        srcSet={srcSet}
        alt={alt}
        className={index === selectedIndex ? 'embla__slide__img active' : 'embla__slide__img'}
        css={(theme: Theme) => carouselSlideImg(objectFit)}
      />
    );
  } else {
    const { content, ...rest } = props;
    innerContent = (
      <div css={(theme: Theme) => carouselSlideText(theme, rest)}>
        <div css={carouselSlideTextInner}>{content}</div>
      </div>
    );
  }

  return (
    <div
      className="embla__slide"
      key={index}
      css={carouselSlide}
      onClick={onClick && (() => onClick(index))}
      role={role}
      aria-label={ariaLabel}
    >
      <div className="embla__slide__inner" css={carouselSlideInner}>
        {innerContent}
      </div>
    </div>
  );
};

export const Carousel: FunctionComponent<CarouselProps> = ({
  buttonPadding = 1,
  objectFit = 'cover',
  slides,
  loop = false,
  slideClickHandling,
  onSlideChanged,
  showArrowsOnMobile = false,
  showControlsForSingleSlide = false,
  showArrowsDesktop = 'never',
  numDotsToDisplay = 5,
  showButtonGradient = true,
}) => {
  const [viewportRef, embla] = useEmblaCarousel({ loop });
  const [prevBtnDisabled, setPrevButtonDisabled] = useState(false);
  const [nextBtnDisabled, setNextButtonDisabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMouseOver, setIsMouseOver] = useState(false);

  const scrollPrev = () => embla?.scrollPrev();
  const scrollNext = () => embla?.scrollNext();
  const scrollTo = (index: number) => embla?.scrollTo(index);

  const dotsScrollOffset = () => {
    // 30 px dot width + 4px horz padding
    const dotWidth = 38;

    // don't being pagination until advancing to 4th dot
    if (selectedIndex < Math.ceil(numDotsToDisplay / 2)) {
      return 0;
    }

    // don't continue pagination if within 1 page of end
    if (selectedIndex >= slides.length - Math.floor(numDotsToDisplay / 2)) {
      return (slides.length - numDotsToDisplay) * dotWidth;
    }

    // paginate with index offset
    return (selectedIndex - Math.floor(numDotsToDisplay / 2)) * dotWidth;
  };

  const onSlideClick =
    slideClickHandling &&
    ((index: number) => {
      // The clickAllowed() check below will allow us to avoid interpreting the beginning of a drag
      // as a click.
      if (embla?.clickAllowed()) {
        slideClickHandling.onClick(index);
      }
    });
  const onSelect = useCallback(() => {
    if (!embla) {
      return;
    }
    const selectedIndex = embla.selectedScrollSnap();
    setSelectedIndex(selectedIndex);
    onSlideChanged?.(selectedIndex);

    setPrevButtonDisabled(!embla.canScrollPrev());
    setNextButtonDisabled(!embla.canScrollNext());
  }, [embla, onSlideChanged]);

  useEffect(() => {
    if (!embla) {
      return;
    }
    onSelect();
    embla.on('select', onSelect);

    return () => {
      embla.off('select', onSelect);
    };
  }, [embla, onSelect]);

  const showControls = showControlsForSingleSlide || slides.length > 1;

  const handleMouseEntry = () => setIsMouseOver(true);
  const handleMouseExit = () => setIsMouseOver(false);

  const shouldShowArrows =
    showArrowsDesktop === 'onmouseover' ? isMouseOver : showArrowsDesktop === 'always';

  const showPointerCursor = !!slideClickHandling && !!slideClickHandling.onClick;

  return (
    <div
      className="embla"
      css={carouselBase(showPointerCursor)}
      onMouseOver={handleMouseEntry}
      onFocus={handleMouseEntry}
      onMouseOut={handleMouseExit}
      onBlur={handleMouseExit}
    >
      <div
        className="embla__viewport"
        css={[carouselViewport, showButtonGradient && carouselButtonGradient]}
        ref={viewportRef}
      >
        <div className="embla__container" css={carouselContainer}>
          {slides.map((slideProps, index) => (
            <SlideDetails
              {...slideProps}
              key={index}
              index={index}
              onClick={onSlideClick}
              // Weirdly, if I try to use optional chaining below the linter barfs.
              role={slideClickHandling ? slideClickHandling.role : undefined}
              aria-label={slideClickHandling ? slideClickHandling['aria-label'] : undefined}
              selectedIndex={selectedIndex}
              objectFit={objectFit}
            />
          ))}
        </div>
      </div>

      {showControls && (
        <>
          <div className="embla__dots" css={dotBtnsContainer(numDotsToDisplay)}>
            <div
              css={[
                dotBtnsContainerInner,
                css`
                  transform: translateX(-${dotsScrollOffset()}px);
                `,
              ]}
            >
              {slides.map((_, index) => (
                <DotButton
                  key={index}
                  selected={index === selectedIndex}
                  onClick={() => scrollTo(index)}
                  slideNum={index + 1}
                />
              ))}
            </div>
          </div>
          <PrevButton
            disabled={prevBtnDisabled}
            onClick={scrollPrev}
            padding={buttonPadding}
            showOnMobile={showArrowsOnMobile}
            show={shouldShowArrows}
          />
          <NextButton
            disabled={nextBtnDisabled}
            onClick={scrollNext}
            padding={buttonPadding}
            showOnMobile={showArrowsOnMobile}
            show={shouldShowArrows}
          />
        </>
      )}
    </div>
  );
};

export default Carousel;
